import { useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { isEmpty } from 'lodash-es'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  PaymentMethods,
} from 'types/beneficiaries'
import { contactInstrumentAtom } from '../atoms/contactAtoms'
import { DEFAULT_FIAT_INSTRUMENT } from '../constants'
import {
  ContactFiatInstrumentFormData,
  useContactFiatInstrumentSchema,
} from '../schemas/contactFiatInstrumentSchema'
import { FormFieldLabels, Step, Steps } from '../types/fiatInstruments'
import { mapContactInstrumentToFiatInstrumentForm } from '../utils/fiatInstruments'

export const useFiatInstrumentForm = () => {
  const { t } = useTranslation()
  const { enableHybridAddress } = useFeatureFlags()

  const contactInstrument = useAtomValue(contactInstrumentAtom)

  const [step, setStep] = useState<Step>(Steps.CURRENCY_DETAILS)

  const isEditingContactInstrument = !isEmpty(contactInstrument) && !!contactInstrument?.id
  const formSchema = useContactFiatInstrumentSchema({ t, enableHybridAddress })
  const form = useForm<ContactFiatInstrumentFormData>({
    defaultValues: isEditingContactInstrument
      ? mapContactInstrumentToFiatInstrumentForm(contactInstrument)
      : DEFAULT_FIAT_INSTRUMENT,
    resolver: yupFormResolver<ContactFiatInstrumentFormData>(formSchema),
  })

  const { control, formState, clearErrors, resetField, setValue, register, trigger, setError } =
    form

  const formValues = useWatch({ control }) as ContactFiatInstrumentFormData

  const isInternationalBankAccount = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isBusinessBankAccount = formValues?.entityType === BeneficiaryEntityType.BUSINESS

  const getSwiftLabels = () => {
    const { currency, bankCountryCode, paymentMethods } = formValues ?? {}

    switch (currency) {
      case BeneficiaryDetailsCurrency.GBP:
        return {
          accountNumber: t('formFields.accountNumberOrIban'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumberOrIban'),
          bankCode: t('formFields.sortCode'),
          bankCodePlaceholder: t('formFields.placeholder.sortCode'),
        }
      case BeneficiaryDetailsCurrency.USD: {
        const isUSBank = bankCountryCode === 'US'
        return {
          accountNumber: t('formFields.accountNumber'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
          bankCode: isUSBank ? t('formFields.routingNumber') : t('formFields.bankCode'),
          bankCodePlaceholder: isUSBank
            ? t('formFields.placeholder.routingNumber')
            : t('formFields.placeholder.bankCode'),
        }
      }
      case BeneficiaryDetailsCurrency.EUR: {
        const isSepa =
          paymentMethods?.includes(PaymentMethods.SEPA) ||
          paymentMethods?.includes(PaymentMethods.SEPA_INSTANT)
        const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)

        return {
          accountNumber: isSepa ? t('formFields.iban') : t('formFields.accountNumberOrIban'),
          accountNumberPlaceholder: isSepa
            ? t('formFields.placeholder.iban')
            : t('formFields.placeholder.accountNumberOrIban'),
          bankCode: isSwift ? t('formFields.bic') : t('formFields.swiftBankCode'),
          bankCodePlaceholder: isSwift
            ? t('formFields.placeholder.bic')
            : t('formFields.placeholder.swiftBankCode'),
        }
      }
      default:
        return {
          accountNumber: t('formFields.accountNumberOrIban'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumberOrIban'),
          bankCode: t('formFields.swiftBankCode'),
          bankCodePlaceholder: t('formFields.placeholder.swiftBankCode'),
        }
    }
  }

  const getLocalLabels = () => {
    switch (formValues?.currency) {
      case BeneficiaryDetailsCurrency.EUR:
        return {
          accountNumber: t('formFields.iban'),
          accountNumberPlaceholder: t('formFields.placeholder.iban'),
          bankCode: t('formFields.bic'),
          bankCodePlaceholder: t('formFields.placeholder.bic'),
        }
      case BeneficiaryDetailsCurrency.GBP:
        return {
          accountNumber: t('formFields.accountNumberOrIban'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumberOrIban'),
          bankCode: t('formFields.sortCodeBic'),
          bankCodePlaceholder: t('formFields.placeholder.sortCodeBic'),
        }
      case BeneficiaryDetailsCurrency.USD:
        return {
          accountNumber: t('formFields.accountNumber'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
          bankCode: t('formFields.routingNumber'),
          bankCodePlaceholder: t('formFields.placeholder.routingNumber'),
        }
      default:
        return {
          accountNumber: t('formFields.accountNumber'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
          bankCode: t('formFields.bankCode'),
          bankCodePlaceholder: t('formFields.placeholder.bankCode'),
        }
    }
  }

  const formFieldLabels: FormFieldLabels = useMemo(() => {
    if (isInternationalBankAccount) {
      return getSwiftLabels()
    }

    return getLocalLabels()
  }, [formValues?.currency, formValues?.paymentMethods])

  return {
    // steps
    step,
    setStep,
    // form
    formValues,
    formState,
    isInternationalBankAccount,
    isBusinessBankAccount,
    formFieldLabels,
    errors: formState.errors,
    isEditingContactInstrument,
    clearErrors,
    resetField,
    setValue,
    register,
    setError,
    trigger,
    form,
  }
}
